import { API_PATHS } from "../constants/api-paths";
import { FPR_DATA_STORAGE_KEY } from "../constants/fpr";
import { COMMON_SSO_MOBILE } from "../constants/sso";
import { logErrorToNewRelic } from "../utils/logging";
import { decryptLocalStorageMobileNo } from "@repo/shared-utils/crypto";
import { getCustomerDemogFromApp } from "@repo/shared-utils/common-utils";
import { IResponse, encryptedRequest } from "../core/api.client";
import { ISubscriptionDetailsResponse } from "./fprSubscription.service";

export interface IVerifyDetailsParams {
  customerFullName: string;
  gender: string;
  pan: string;
  city: string;
  pinCode: string;
  dob: string;
  employmentType: string;
  salary: string;
  gstin: string;
  iAmLookingFor: string;
}

export interface IVerifyDetailsResponse extends ISubscriptionDetailsResponse {
  paymentGatewayURL: string;
  paymentGatewayParameters: string;
  fprResult: string | null;
  fprpreviewdata: {
    result: IFprReportPreviewResult;
  };
  fprReportDashboard: {
    result: IFprReportPreviewResult;
    timestamp: string;
  };
  paymentTxnReferenceNumber: string;
}

interface IFprReportPreviewResult {
  creditScore: {
    score: number;
    scoreCategory: string;
  };
  creditScoreRanges: {
    scoreCategory: string;
    scoreRange: string;
  };
  creditInsights: {
    paymentHistory: number;
    ccUtilisation: number;
    ageOfAccount: number;
    creditEnquiries: number;
    loanOutstanding: number;
  };
  paymentHistory: {
    totalDuePayment: number;
    totalOnTimePayment: number;
    dateProcessed: string;
    totalPayment: number;
  };
  creditCardUtilisation: {
    totalCreditLimit: number;
    totalUtilizedCreditLimit: number;
    dateProcessed: string;
  };
  loanOutstanding: {
    totalSanctionedAmount: number;
    totalBalanceAmount: number;
  };
  accountSummary: {
    activeAccount: number;
    closedAccount: number;
    loanDetails?: LoanDetailsEntity[] | null;
  };
  AgeOfAccount: {
    oldestAccountAge: number;
    newestAccountAge: number;
  };
  creditEnquiries: {
    totalLoansEnquiries: number;
    totalCardsEnquiries: number;
    mostRecentEnquiries: [
      {
        "Enquiry Date": string;
        "Enquiry Purpose": string;
        "Enquiry Amount": number;
        "Enquiry Member": string;
      },
    ];
  };
}

export interface LoanDetailsEntity {
  cashLimit: string;
  emiamount: string;
  dateClosed: string;
  accountNumber: string;
  amountOverdue: string;
  currentBalance: string;
  rateOfInterest: string;
  accountTypeDesc: string;
  dateLastPayment: string;
  paymentHistory1: string;
  paymentHistory2: string;
  repaymentTenure: string;
  paymentFrequency: string;
  typeOfCollateral: string;
  valueOfCollateral: string;
  reportingShortName: string;
  dateReportedAndCert: string;
  dateOpenedOrDisbursed: string;
  highCreditSanctionAmt: string;
  paymentHistoryEndDate: string;
  paymentHistoryStartDate: string;
  creditLimit: string;
}

export const verifyDetails = async (
  params: IVerifyDetailsParams,
  csRequestId?: string
): Promise<IResponse<IVerifyDetailsResponse>> => {
  try {
    const localStorageMobileNo = localStorage.getItem(COMMON_SSO_MOBILE);
    const mobileNo = await decryptLocalStorageMobileNo(localStorageMobileNo || "");
    const response = encryptedRequest<IVerifyDetailsResponse>({
      method: "POST",
      url: API_PATHS.verifyDetails,
      data: {
        ...params,
        mobileNo: mobileNo || getCustomerDemogFromApp().mobileNo,
        csRequestId, // Pass as parameter instead of reading from signal
      },
      storeResponseInLocal: true,
      storageKey: FPR_DATA_STORAGE_KEY,
    });

    return response;
  } catch (error) {
    logErrorToNewRelic(error as Error);
    return {} as IResponse<IVerifyDetailsResponse>;
  }
};
